import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { Building, Plus, X, CheckCircle2, Search } from 'lucide-react';

// Sprint 76 (3/N) — Gestión condominal. El backend (condo.py, Sprint 75
// 12/N) llevaba sprints funcionando sin ninguna pantalla de tenant.
const STATUS_STYLES = {
  pending: { label: 'Pendiente', bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-600' },
  overdue: { label: 'Vencida', bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700' },
  paid: { label: 'Pagada', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700' },
};

export default function CondoManagement() {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState({ amount: '', period_month: '' });
  const [generating, setGenerating] = useState(false);
  const [reconcilingId, setReconcilingId] = useState(null);

  const [leads, setLeads] = useState([]);
  const [resources, setResources] = useState([]);
  const [lookupLeadId, setLookupLeadId] = useState('');
  const [accountStatus, setAccountStatus] = useState(null);
  const [checkingAccount, setCheckingAccount] = useState(false);

  const [showReserve, setShowReserve] = useState(false);
  const [reserveForm, setReserveForm] = useState({ crm_lead_id: '', resource_id: '', starts_at: '', title: '' });
  const [reserving, setReserving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/condo/fees');
      setFees(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar las cuotas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/tenant/crm/leads').then(r => setLeads(r.data?.items || r.data || [])).catch(() => setLeads([]));
    api.get('/tenant/agenda/resources').then(r => setResources(r.data || [])).catch(() => setResources([]));
  }, []);

  const handleGenerate = async () => {
    if (!genForm.amount || Number(genForm.amount) <= 0) { toast.error('Pon un monto de cuota válido'); return; }
    setGenerating(true);
    try {
      const { data } = await api.post('/tenant/condo/fees/generate', {
        amount: Number(genForm.amount),
        period_month: genForm.period_month || undefined,
      });
      toast.success(`${data.created} cuota(s) generada(s) para las unidades vendidas`);
      setShowGenerate(false);
      setGenForm({ amount: '', period_month: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron generar las cuotas'); }
    finally { setGenerating(false); }
  };

  const handleReconcile = async (feeId) => {
    setReconcilingId(feeId);
    try {
      await api.post(`/tenant/condo/fees/${feeId}/reconcile`, {});
      toast.success('Cuota marcada como pagada');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo confirmar el pago'); }
    finally { setReconcilingId(null); }
  };

  const handleCheckAccount = async () => {
    if (!lookupLeadId) { toast.error('Elige un lead para consultar'); return; }
    setCheckingAccount(true);
    setAccountStatus(null);
    try {
      const { data } = await api.get(`/tenant/condo/account-status/${lookupLeadId}`);
      setAccountStatus(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo consultar la cuenta'); }
    finally { setCheckingAccount(false); }
  };

  const handleReserveAmenity = async () => {
    if (!reserveForm.crm_lead_id || !reserveForm.resource_id || !reserveForm.starts_at) {
      toast.error('Completa lead, amenidad y fecha'); return;
    }
    setReserving(true);
    try {
      await api.post('/tenant/condo/amenities/reserve', {
        crm_lead_id: reserveForm.crm_lead_id, resource_id: reserveForm.resource_id,
        starts_at: new Date(reserveForm.starts_at).toISOString(),
        duration_minutes: 60, title: reserveForm.title || undefined,
      });
      toast.success('Amenidad reservada — ya aparece en Agenda');
      setShowReserve(false);
      setReserveForm({ crm_lead_id: '', resource_id: '', starts_at: '', title: '' });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo reservar — verifica que la cuenta esté al corriente'); }
    finally { setReserving(false); }
  };

  return (
    <div className="space-y-5" data-testid="condo-management-page">
      <div>
        <h2 className="text-sm font-bold flex items-center gap-2">
          <Building className="w-4 h-4" /> Gestión condominal
          <HelpTooltip text="Cuotas de mantenimiento mensuales por unidad vendida y reserva de amenidades — el sistema bloquea reservas si el propietario tiene cuotas vencidas." />
        </h2>
        <p className="text-xs text-[#64748B] mt-0.5">Genera cuotas, concilia pagos, consulta si un propietario está al corriente, y reserva amenidades.</p>
      </div>

      <SectionHelp
        module="condo_management"
        title="Gestión condominal"
        description="Genera una cuota mensual por cada unidad vendida (solo unidades con plan de pagos completado). Un propietario con cuotas vencidas no puede reservar amenidades hasta ponerse al corriente."
        whatItDoesNot="No calcula prorrateo por m² ni cuotas variables por tipo de unidad — el monto que pongas aplica igual a todas las unidades del periodo."
      />

      {/* Cuotas */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-[#334155]">Cuotas de mantenimiento</p>
          <Button size="sm" onClick={() => setShowGenerate(!showGenerate)} data-testid="generate-fees-btn">
            <Plus className="w-4 h-4 mr-1" /> Generar cuotas del mes
          </Button>
        </div>

        {showGenerate && (
          <div className="bg-white border rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-semibold">Generar cuotas mensuales</h4>
              <button onClick={() => setShowGenerate(false)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[#64748B]">Monto por unidad</label>
                <Input type="number" placeholder="1500" value={genForm.amount} onChange={e => setGenForm({ ...genForm, amount: e.target.value })} data-testid="generate-fees-amount" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Periodo (opcional, default mes actual)</label>
                <Input type="month" value={genForm.period_month} onChange={e => setGenForm({ ...genForm, period_month: e.target.value ? `${e.target.value}-01` : '' })} />
              </div>
            </div>
            <p className="text-[11px] text-[#94A3B8]">Es seguro correrlo varias veces — no duplica cuotas ya generadas para el mismo periodo.</p>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowGenerate(false)}>Cancelar</Button>
              <Button size="sm" onClick={handleGenerate} disabled={generating} data-testid="generate-fees-save">{generating ? 'Generando...' : 'Generar'}</Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-xs text-[#94A3B8] text-center py-6">Cargando...</p>
        ) : fees.length === 0 ? (
          <div className="bg-white border rounded-xl p-6 text-center">
            <Building className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
            <p className="text-sm text-[#64748B]">Todavía no hay cuotas generadas.</p>
          </div>
        ) : (
          <div className="bg-white border rounded-xl divide-y" data-testid="condo-fees-list">
            {fees.map(f => {
              const style = STATUS_STYLES[f.status] || STATUS_STYLES.pending;
              return (
                <div key={f.id} className="flex items-center justify-between p-3 text-xs" data-testid={`condo-fee-${f.id}`}>
                  <div>
                    <p className="font-medium text-[#0F172A]">{f.lead_name}{f.unit_name ? ` · ${f.unit_name}` : ''}</p>
                    <p className="text-[#64748B]">{f.period_month} · ${Number(f.amount).toLocaleString('es-MX')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full border ${style.bg} ${style.border} ${style.text}`}>{style.label}</span>
                    {f.status !== 'paid' && (
                      <button onClick={() => handleReconcile(f.id)} disabled={reconcilingId === f.id}
                        className="text-emerald-600 hover:underline" data-testid={`condo-fee-reconcile-${f.id}`}>
                        {reconcilingId === f.id ? 'Confirmando...' : 'Confirmar pago'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Estado de cuenta */}
      <div className="bg-white border rounded-xl p-4 space-y-2">
        <p className="text-xs font-bold text-[#334155] flex items-center gap-1"><Search className="w-3.5 h-3.5" /> Consultar estado de cuenta</p>
        <div className="flex gap-2">
          <Select value={lookupLeadId} onValueChange={setLookupLeadId}>
            <SelectTrigger className="h-8 text-xs" data-testid="condo-lookup-lead"><SelectValue placeholder="Elige un propietario..." /></SelectTrigger>
            <SelectContent>{leads.map(l => <SelectItem key={l.id} value={l.id}>{l.display_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={handleCheckAccount} disabled={checkingAccount} data-testid="condo-lookup-check">
            {checkingAccount ? 'Consultando...' : 'Consultar'}
          </Button>
        </div>
        {accountStatus && (
          <div className={`text-xs p-2 rounded-lg flex items-center gap-1.5 ${accountStatus.al_corriente ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="condo-account-status-result">
            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
            {accountStatus.al_corriente ? 'Al corriente — puede reservar amenidades.'
              : accountStatus.motivo === 'no_unit_linked' ? 'Este lead no tiene una unidad vendida a su nombre.'
              : 'Tiene cuotas vencidas — debe ponerse al corriente para reservar amenidades.'}
          </div>
        )}
      </div>

      {/* Reserva de amenidades */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-[#334155]">Reserva de amenidades</p>
          <Button size="sm" variant="outline" onClick={() => setShowReserve(!showReserve)} data-testid="reserve-amenity-btn">
            <Plus className="w-4 h-4 mr-1" /> Nueva reserva
          </Button>
        </div>
        {showReserve && (
          <div className="bg-white border rounded-xl p-4 space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[#64748B]">Propietario</label>
                <Select value={reserveForm.crm_lead_id} onValueChange={v => setReserveForm({ ...reserveForm, crm_lead_id: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="reserve-amenity-lead"><SelectValue placeholder="Elige..." /></SelectTrigger>
                  <SelectContent>{leads.map(l => <SelectItem key={l.id} value={l.id}>{l.display_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Amenidad</label>
                <Select value={reserveForm.resource_id} onValueChange={v => setReserveForm({ ...reserveForm, resource_id: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="reserve-amenity-resource"><SelectValue placeholder="Elige..." /></SelectTrigger>
                  <SelectContent>{resources.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Fecha y hora</label>
                <Input type="datetime-local" value={reserveForm.starts_at} onChange={e => setReserveForm({ ...reserveForm, starts_at: e.target.value })} data-testid="reserve-amenity-date" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Título (opcional)</label>
                <Input placeholder="Reserva de amenidad" value={reserveForm.title} onChange={e => setReserveForm({ ...reserveForm, title: e.target.value })} />
              </div>
            </div>
            <p className="text-[11px] text-[#94A3B8]">Se rechaza automáticamente si el propietario tiene cuotas vencidas.</p>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowReserve(false)}>Cancelar</Button>
              <Button size="sm" onClick={handleReserveAmenity} disabled={reserving} data-testid="reserve-amenity-save">{reserving ? 'Reservando...' : 'Reservar'}</Button>
            </div>
          </div>
        )}
        {resources.length === 0 && !showReserve && (
          <p className="text-[11px] text-[#94A3B8]">No hay amenidades registradas todavía — créalas en Mi Negocio → Catálogo/Recursos (ej. "Salón de eventos", "Alberca").</p>
        )}
      </div>
    </div>
  );
}
